import { useEffect, type CSSProperties } from "react";
import { showToast } from "../primitives";
import { useMcpEndpoints, type McpEndpoint } from "../../hooks/use-mcp-endpoints";

/**
 * Sprint 17 — the Cmd-K "Copy MCP endpoint URL" picker. Lists every endpoint from
 * {@link useMcpEndpoints}; selecting one copies its absolute URL and closes. Hosted
 * by the command palette (like the create / prompt modals) so it survives the
 * palette closing.
 */
export function CopyMcpEndpointModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data, isLoading } = useMcpEndpoints();

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const endpoints = data?.endpoints ?? [];

  async function pick(ep: McpEndpoint) {
    try {
      await navigator.clipboard.writeText(ep.url);
      showToast(`Copied ${ep.path} URL`);
    } catch {
      showToast("Couldn't copy to clipboard", "error", true);
    }
    onClose();
  }

  return (
    <div
      style={overlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div style={box} role="dialog" aria-label="Copy MCP endpoint URL">
        <div style={header}>
          <i className="ti ti-plug" style={{ fontSize: 15 }} aria-hidden="true" />
          <span style={{ fontSize: 14, fontWeight: 500 }}>Copy MCP endpoint URL</span>
        </div>
        <div style={list}>
          {isLoading && <div style={muted}>Loading endpoints…</div>}
          {!isLoading && endpoints.length === 0 && (
            <div style={muted}>No endpoints available. Connect an MCP session first.</div>
          )}
          {endpoints.map((ep) => (
            <button key={ep.path} onClick={() => pick(ep)} style={item}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={itemLabel}>{ep.label}</span>
                <code style={itemPath}>{ep.path}</code>
                <span style={{ flex: 1 }} />
                <span style={itemCount}>{ep.tool_count}</span>
                <i className="ti ti-copy" style={{ fontSize: 14, color: "var(--color-text-tertiary)" }} aria-hidden="true" />
              </div>
              <div style={itemDesc}>{ep.url}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.3)",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  paddingTop: "14vh",
  zIndex: 320,
  fontFamily: "var(--font-sans)",
};

const box: CSSProperties = {
  width: 560,
  maxWidth: "92vw",
  background: "var(--color-background-primary)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-lg)",
  boxShadow: "0 12px 48px rgba(0,0,0,0.18)",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

const header: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "12px 16px",
  borderBottom: "var(--kuvio-border-default)",
  color: "var(--color-text-primary)",
};

const list: CSSProperties = {
  maxHeight: "56vh",
  overflowY: "auto",
  padding: 6,
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const item: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 3,
  width: "100%",
  textAlign: "left",
  padding: "9px 10px",
  border: "none",
  borderRadius: "var(--border-radius-md)",
  background: "transparent",
  cursor: "pointer",
};

const itemLabel: CSSProperties = {
  fontSize: 13.5,
  fontWeight: 500,
  color: "var(--color-text-primary)",
};

const itemPath: CSSProperties = {
  fontSize: 11.5,
  fontFamily: "var(--font-mono, ui-monospace, monospace)",
  color: "var(--color-text-tertiary)",
};

const itemCount: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  fontVariantNumeric: "tabular-nums",
};

const itemDesc: CSSProperties = {
  fontSize: 11.5,
  color: "var(--color-text-tertiary)",
  fontFamily: "var(--font-mono, ui-monospace, monospace)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const muted: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-tertiary)",
  padding: "16px 12px",
  textAlign: "center",
};
